import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './MainScreens.css';
import './SmartSearchScreen.css';

export default function SmartSearchScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [geoLocation, setGeoLocation] = useState(null);
  const [geoError, setGeoError] = useState('');

  React.useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          setGeoError('');
        },
        (err) => {
          console.warn('Geolocation error:', err.message);
          setGeoError('Location access denied. Results may be less accurate.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGeoError('Geolocation not supported by your browser.');
    }
  }, []);

  const exampleQueries = [
    "I want spicy tacos under $15",
    "Looking for a romantic Italian place for date night",
    "Quick lunch spot with healthy options nearby",
    "Best sushi within 5 miles",
    "Cheap pizza place that's open now",
    "Fancy steakhouse for anniversary dinner"
  ];

  async function handleSearch() {
    if (!query.trim()) {
      setError('Please describe what you\'re looking for');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/restaurants/smart-search', {
        query: query.trim(),
        latitude: geoLocation?.latitude,
        longitude: geoLocation?.longitude,
        userId: user?.id,
      });

      if (!data.restaurants || data.restaurants.length === 0) {
        setError('No restaurants found matching your request. Try being more specific or broadening your search.');
        setLoading(false);
        return;
      }

      // Create a solo session with the AI-matched restaurants
      const sessionResponse = await api.post('/sessions/create', {
        mode: 'solo',
        maxParticipants: 1,
        restaurants: data.restaurants,
        preferences: {
          smartSearch: true,
          query: query.trim(),
          aiMatched: true
        },
      });

      navigate(`/session/${sessionResponse.data.sessionCode}`, { replace: true });
    } catch (err) {
      console.error('Smart search error:', err);
      if (err.response?.status === 429 && err.response?.data?.limit_reached) {
        const d = err.response.data;
        setLoading(false);
        const goUpgrade = window.confirm(
          `🔒 Weekly Search Limit Reached\n\n` +
          `You've used ${d.searches_used}/${d.searches_limit} free searches this week.\n\n` +
          `Upgrade to Premium for:\n` +
          `✓ Unlimited searches\n` +
          `✓ 3% cashback on visits\n` +
          `✓ Gift card redemptions\n` +
          `✓ Spin the Wheel rewards\n\n` +
          `Resets: ${new Date(d.resets_at).toLocaleDateString()}\n\n` +
          `Upgrade now?`
        );
        if (goUpgrade) navigate('/subscription');
        return;
      }
      setError(err.response?.data?.error || err.message || 'Failed to search. Please try again.');
      setLoading(false);
    }
  }

  function handleExampleClick(example) {
    setQuery(example);
  }

  return (
    <div className="main-screen">
      <MainHeader title="Smart Search" showBack={true} backPath="/home" />

      <main className="screen-main">
        <div className="smart-search-container">
          {geoError && (
            <div className="pref-warning">
              📍 {geoError}
            </div>
          )}

          {error && (
            <div className="pref-error">{error}</div>
          )}

          <div className="smart-search-hero">
            <div className="smart-search-icon">🤖</div>
            <h1 className="smart-search-title">Tell us what you want</h1>
            <p className="smart-search-subtitle">
              Describe your ideal meal in plain English and we'll find the perfect match
            </p>
          </div>

          <div className="smart-search-input-section">
            <textarea
              className="smart-search-input"
              placeholder="e.g., I want spicy tacos under $15 within 3 miles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              rows={4}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <button
              className="btn btn-primary btn-lg smart-search-btn"
              onClick={handleSearch}
              disabled={loading || !query.trim()}
            >
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner-small"></span> Searching...
                </span>
              ) : (
                <>🔍 Find Restaurants</>
              )}
            </button>
          </div>

          <div className="smart-search-examples">
            <h3 className="examples-title">Try these examples:</h3>
            <div className="examples-grid">
              {exampleQueries.map((example, idx) => (
                <button
                  key={idx}
                  className="example-chip"
                  onClick={() => handleExampleClick(example)}
                  disabled={loading}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          <div className="smart-search-info">
            <div className="info-card">
              <div className="info-icon">✨</div>
              <div className="info-content">
                <h4>AI-Powered Matching</h4>
                <p>Our AI understands your preferences and finds restaurants that match your exact needs</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon">🎯</div>
              <div className="info-content">
                <h4>Smart Results</h4>
                <p>Get the best matches based on cuisine, price, distance, mood, and availability</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
